
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';

export default function MessagesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchConversations();
  }, [user]);

  const fetchConversations = async () => {
    try {
      setLoading(true);

      // Fetch conversations where user is a participant
      const { data: convos, error: convosError } = await supabase
        .from('conversations')
        .select('*')
        .or(`participant1_id.eq.${user.id},participant2_id.eq.${user.id}`)
        .order('updated_at', { ascending: false });

      if (convosError) throw convosError;

      if (!convos || convos.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
      }

      // Get all other participant IDs
      const otherUserIds = convos.map(c => 
        c.participant1_id === user.id ? c.participant2_id : c.participant1_id
      );

      // Fetch other users' profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', otherUserIds);

      // Fetch last message for each conversation
      const conversationIds = convos.map(c => c.id);
      const { data: messages } = await supabase
        .from('messages')
        .select('conversation_id, content, created_at')
        .in('conversation_id', conversationIds)
        .order('created_at', { ascending: false });

      // Fetch unread counts
      const { data: unreadMessages } = await supabase
        .from('messages')
        .select('conversation_id')
        .in('conversation_id', conversationIds)
        .eq('read', false)
        .neq('sender_id', user.id);

      // Combine data
      const enrichedConvos = convos.map(convo => {
        const otherUserId = convo.participant1_id === user.id 
          ? convo.participant2_id 
          : convo.participant1_id;
        
        const otherUser = profiles?.find(p => p.id === otherUserId);
        
        // Find last message for this conversation
        const lastMsg = messages?.find(m => m.conversation_id === convo.id);
        
        // Count unread messages
        const unreadCount = unreadMessages?.filter(
          m => m.conversation_id === convo.id
        ).length || 0;

        return {
          ...convo,
          otherUser,
          lastMessage: lastMsg?.content,
          unreadCount
        };
      });

      setConversations(enrichedConvos);
    } catch (_error: unknown) {
      // silently handle error
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await supabase.from('messages').delete().eq('conversation_id', conversationId);
      await supabase.from('conversations').delete().eq('id', conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
    } catch (_error: unknown) {
      // silently handle error
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-32 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-4 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-600 mt-1">Chat with hosts and renters</p>
          </div>

          {/* Conversations List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {loading ? (
              <div className="p-8 text-center">
                <i className="ri-loader-4-line text-2xl text-gray-400 animate-spin"></i>
                <p className="mt-2 text-gray-600">Loading conversations...</p>
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center">
                <i className="ri-mail-line text-4xl text-gray-300 mb-2"></i>
                <p className="text-gray-600">No conversations yet</p>
                <p className="text-sm text-gray-500 mt-1">Start a conversation with a host or renter</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {conversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/messages/${conversation.id}`)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-blue-600 text-xl"></i>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`truncate ${conversation.unreadCount > 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
                            {conversation.otherUser?.full_name || 'User'}
                          </p>
                          <span className="text-xs text-gray-500">
                            {new Date(conversation.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className={`text-sm truncate mt-1 ${conversation.unreadCount > 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                          {conversation.lastMessage || 'No messages yet'}
                        </p>
                      </div>

                      {/* Unread badge */}
                      {conversation.unreadCount > 0 && (
                        <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center flex-shrink-0">
                          {conversation.unreadCount}
                        </div>
                      )}

                      <button
                        onClick={(e) => deleteConversation(conversation.id, e)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                        title="Delete conversation"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
